// External libraries
import React, { useEffect, useReducer } from "react";
import Select from "react-select";
import { useQueryClient } from "@tanstack/react-query";

// Project components
import Button from "../../../../Components/Button";
import Grid from "../../../../Components/Grid";
import Modal from "../../../../Components/Modal";
import Multiselect from "../../../../Components/Inputs/Multiselect";
import TextInput from "../../../../Components/Inputs/Text";
import Spacer from "../../../../Components/Spacer";
import { Text } from "../../../../Components/Text";
import Spinner from "../../../../Components/Spinner";

// Feature/context-specific hooks and constants
import { useNavigation } from "../../../../Hooks/useNavigation";
import { useSeedConfigurations } from "../../../../Api/hooks";
import { useConfiguration } from "../../contexts/ConfigurationContext";
import { ENVIRONMENTS, SEED_CONFIGURATION_OPTIONS } from "../../contexts/constants";
import { createWorkflowDispatchEvent } from "../../../../Api/services/seeder";
import { createCompanyLog } from "../../contexts/api";

// Styles
import css from "../styles.styl";

// Local/relative feature components
import RandomizedDataSection from "./RandomizedData";
import SelectConfigTable from "./tables/SelectConfigTable";

/**
 * @typedef {Object} ModalState
 * @property {boolean} isOpen - Whether the modal is open
 * @property {boolean} isError - Whether the modal is an error modal
 * @property {boolean} isCompanyCreated - Whether the company creation process is complete
 * @property {string} message - The message to display in the modal
 * @property {Function|null} postAction - Action to execute after modal closes
 */

/**
 * @typedef {Object} ModalAction
 * @property {string} type - The type of modal action
 * @property {Object} payload - The payload for the action
 */

/**
 * Modal state reducer
 * @param {ModalState} state - Current modal state
 * @param {ModalAction} action - Action to perform
 * @returns {ModalState} New modal state
 */
const modalReducer = (state, action) => {
	switch (action.type) {
		case "OPEN_MODAL":
			return {
				...state,
				isOpen: true,
				isError: false,
				message: action.payload?.message || state.message,
				isCompanyCreated: false,
			};
		case "CLOSE_MODAL":
			return {
				...state,
				isOpen: false,
				isError: false,
				message: "Creating company...",
				isCompanyCreated: false,
				postAction: null,
			};
		case "SET_COMPANY_CREATED":
			return {
				...state,
				isCompanyCreated: true,
				message: action.payload?.message || state.message,
			};
		case "SET_POST_ACTION":
			return {
				...state,
				postAction: action.payload?.postAction || null,
			};
		case "ERROR_MODAL":
			return {
				...state,
				isOpen: true,
				isError: true,
				message: action.payload?.message || state.message,
			};
		default:
			return state;
	}
};

/**
 * Resets all configuration and data states to their initial values
 * @param {Function} setConfig - Function to update configuration state
 * @param {Function} resetAllCustomizedData - Function to reset customized data
 * @param {Function} resetRandomDataConfig - Function to reset random data
 */
const resetConfigAndData = (setConfig, resetAllCustomizedData, resetRandomDataConfig) => {
	setConfig({
		seedConfiguration: null,
		seedConfigurationName: "",
		seedCompanyName: "",
		environment: ENVIRONMENTS[0],
		seedConfigurationOptions: [],
	});
	resetAllCustomizedData();
	resetRandomDataConfig();
};

/**
 * Handles changes to the configuration name
 * @param {string} value - New configuration name
 * @param {Object} config - Current configuration
 * @param {Function} setConfig - Function to update configuration
 */
const handleConfigNameChange = (value, config, setConfig) => {
	setConfig({ ...config, seedConfigurationName: value });
};

/**
 * Handles changes to the environment selection
 * @param {Object} value - Selected environment option
 * @param {Object} config - Current configuration
 * @param {Function} setConfig - Function to update configuration
 */
const handleEnvironmentChange = (value, config, setConfig) => {
	setConfig({ ...config, environment: value });
};

/**
 * Handles changes to configuration options
 * @param {Array} values - Selected configuration options
 * @param {Object} config - Current configuration
 * @param {Function} setConfig - Function to update configuration
 */
const handleConfigOptionsChange = (values, config, setConfig) => {
	setConfig({ ...config, seedConfigurationOptions: values });
};

/**
 * Component for managing seed configuration settings
 * @param {Object} props
 * @param {string} props.view - The current view name
 * @param {string} props.configId - The configuration ID if editing an existing configuration
 * @returns {JSX.Element} Rendered component
 */
const SeedConfigurationSettings = ({ view, configId }) => {
	const [modalState, dispatchModal] = useReducer(modalReducer, {
		isOpen: false,
		isError: false,
		isCompanyCreated: false,
		message: "Creating company...",
		postAction: null,
	});

	const navigation = useNavigation();
	const queryClient = useQueryClient();
	const { refetch: refetchConfigurations } = useSeedConfigurations();
	const {
		config,
		setConfig,
		createConfiguration,
		updateConfiguration,
		deleteConfiguration,
		getConfigurationById,
		resetAllCustomizedData,
		resetRandomDataConfig,
		copyConfiguration,
		combineData,
		createSeededCompany,
		getWorkflowRunStatusById,
		getWorkflowRunLogsById,
	} = useConfiguration();

	/**
	 * Polls the workflow run status until it completes or times out
	 * @param {string} runId - The workflow run ID to poll
	 * @param {number} interval - Polling interval in milliseconds
	 * @param {number} maxAttempts - Maximum number of polling attempts
	 * @returns {Promise<string>} The final workflow status
	 * @throws {Error} If the workflow run does not complete in time
	 */
	const pollWorkflowRunUntilCompleted = async (runId, interval = 3000, maxAttempts = 40) => {
		let attempts = 0;
		while (attempts < maxAttempts) {
			const status = await getWorkflowRunStatusById(runId);
			if (status === "queued") {
				dispatchModal({
					type: "OPEN_MODAL",
					payload: { message: "Company creation is queued..." },
				});
			}
			if (status === "in_progress") {
				dispatchModal({
					type: "OPEN_MODAL",
					payload: { message: "Company creation is in progress..." },
				});
			}
			if (status === "completed") {
				return status;
			}
			await new Promise((resolve) => setTimeout(resolve, interval));
			attempts++;
		}
		throw new Error("Workflow run did not complete in time");
	};

	/**
	 * Handles the creation of a new configuration and company
	 * @param {Object} config - Current configuration
	 * @param {Function} createConfiguration - Function to create configuration
	 * @param {Function} dispatchModal - Function to dispatch modal
	 * @param {Object} handlers - Object containing reset and navigation handlers
	 */
	const handleCreateConfigAndCompany = async (
		config,
		createConfiguration,
		dispatchModal,
		{ setConfig, resetAllCustomizedData, resetRandomDataConfig, navigation, view }
	) => {
		const configWithFullOptions = {
			...config,
			seedConfigurationOptions: config.seedConfigurationOptions
				.map((optionId) => SEED_CONFIGURATION_OPTIONS.find((option) => option.id === optionId))
				.filter(Boolean),
		};

		try {
			dispatchModal({
				type: "OPEN_MODAL",
				payload: { message: "Creating configuration..." },
			});
			const completedConfig = await createConfiguration(configWithFullOptions);
			const stringifiedConfig = JSON.stringify(completedConfig);
			const response = await createWorkflowDispatchEvent(stringifiedConfig);
			const workflowRunId = response?.data?.data?.runId;
			if (!workflowRunId) {
				throw new Error("No workflow run ID returned from the server");
			}
			await pollWorkflowRunUntilCompleted(workflowRunId);
			const companyId = await getWorkflowRunLogsById(workflowRunId);
			dispatchModal({
				type: "SET_COMPANY_CREATED",
				payload: { message: `Company name: ${config.seedCompanyName}\nCompany id: ${companyId}` },
			});
			await createCompanyLog(
				companyId,
				config.seedCompanyName,
				config.environment.label,
				new Date().toLocaleString(),
				config.seedConfigurationName
			);
			dispatchModal({
				type: "SET_POST_ACTION",
				payload: {
					postAction: async () => {
						await queryClient.invalidateQueries({ queryKey: ["seed-configurations"] });
						await refetchConfigurations();
						resetConfigAndData(setConfig, resetAllCustomizedData, resetRandomDataConfig);
						navigation.navigate(`/seed-company/${view || "seed-configuration-set-up"}`);
					},
				},
			});
		} catch (error) {
			console.error("Failed to create configuration:", error);
			dispatchModal({
				type: "ERROR_MODAL",
				payload: { message: `Error: ${error.message}` },
			});
		}
	};

	useEffect(() => {
		if (configId && !config.seedConfiguration) {
			const loadConfigFromUrl = async () => {
				try {
					const configData = await getConfigurationById(configId);
					if (configData) {
						setConfig((prev) => ({
							...prev,
							seedConfiguration: {
								seedConfigurationId: configId,
								configName: configData.configName || "",
								...configData,
							},
						}));
					} else {
						console.warn("No configuration data found for ID:", configId);
					}
				} catch (error) {
					console.error("Error loading configuration from URL:", error);
				}
			};
			loadConfigFromUrl();
		}
	}, [configId, config.seedConfiguration, getConfigurationById, setConfig]);

	useEffect(() => {
		return () => dispatchModal({ type: "CLOSE_MODAL" });
	}, []);

	const handleRunConfiguration = async (selectedConfig) => {
		const config = await getConfigurationById(selectedConfig.seedConfigurationId);
		handleCreateOrUpdateCompany("runOnly", config);
	};

	const handleConfigurationChange = (selectedConfig) => {
		setConfig({ ...config, seedConfiguration: selectedConfig });

		if (selectedConfig?.seedConfigurationId) {
			const newUrl = `/seed-company/${view || "seed-configuration-set-up"}/${selectedConfig.seedConfigurationId}`;
			navigation.navigate(newUrl);
		} else {
			navigation.navigate(`/seed-company/${view || "seed-configuration-set-up"}`);
		}
	};

	const handleCopyConfiguration = async (selectedConfig) => {
		try {
			await copyConfiguration(selectedConfig.seedConfigurationId);
			await queryClient.invalidateQueries({ queryKey: ["seed-configurations"] });
			await refetchConfigurations();
		} catch (error) {
			console.error("Failed to copy configuration:", error);
		}
	};

	const handleDeleteConfiguration = async (selectedConfig) => {
		try {
			await deleteConfiguration(selectedConfig.seedConfigurationId);
			await queryClient.invalidateQueries({ queryKey: ["seed-configurations"] });
			resetConfigAndData(setConfig, resetAllCustomizedData, resetRandomDataConfig);
			navigation.navigate("/seed-company/seed-configuration-set-up");
		} catch (error) {
			console.error("Error deleting configuration:", error);
		}
	};

	const handleCreateOrUpdateCompany = async (mode, selectedConfig = null) => {
		try {
			if (mode === "runOnly") {
				const companyName = selectedConfig.company_name;
				dispatchModal({
					type: "OPEN_MODAL",
					payload: { message: "Creating company..." },
				});
				const response = await createSeededCompany(selectedConfig);
				const runId = response?.data?.data?.runId;
				if (!runId) {
					throw new Error("No workflow run ID returned from the server");
				}
				await pollWorkflowRunUntilCompleted(runId);
				const companyId = await getWorkflowRunLogsById(runId);
				dispatchModal({
					type: "SET_COMPANY_CREATED",
					payload: { message: `Company name: ${companyName}\nCompany id: ${companyId}` },
				});
				await createCompanyLog(
					companyId,
					selectedConfig.company_name,
					selectedConfig.environment,
					new Date().toLocaleString(),
					selectedConfig.configName || selectedConfig.config_name
				);
				dispatchModal({
					type: "SET_POST_ACTION",
					payload: {
						postAction: async () => {
							await queryClient.invalidateQueries({ queryKey: ["seed-configurations"] });
							await refetchConfigurations();
							resetConfigAndData(setConfig, resetAllCustomizedData, resetRandomDataConfig);
							navigation.navigate(`/seed-company/${view || "seed-configuration-set-up"}`);
						},
					},
				});
				return;
			} else if (mode === "update") {
				const configWithFullOptions = {
					...config,
					seedConfigurationId: config.seedConfiguration.seedConfigurationId,
					seedConfigurationOptions: config.seedConfigurationOptions
						.map((optionId) => SEED_CONFIGURATION_OPTIONS.find((option) => option.id === optionId))
						.filter(Boolean),
				};
				const companyName = config.seedCompanyName;
				const completedConfiguration = await updateConfiguration(configWithFullOptions);
				dispatchModal({
					type: "OPEN_MODAL",
					payload: { message: "Updating configuration..." },
				});
				const stringifiedConfig = JSON.stringify(completedConfiguration);
				const response = await createWorkflowDispatchEvent(stringifiedConfig);
				const runId = response?.data?.data?.runId;
				if (!runId) {
					throw new Error("No workflow run ID returned from the server");
				}
				await pollWorkflowRunUntilCompleted(runId);
				const companyId = await getWorkflowRunLogsById(runId);

				dispatchModal({
					type: "SET_COMPANY_CREATED",
					payload: { message: `Company name: ${companyName}\nCompany id: ${companyId}` },
				});
				await createCompanyLog(
					companyId,
					companyName,
					config.environment.label,
					new Date().toLocaleString(),
					config.seedConfigurationName
				);
				dispatchModal({
					type: "SET_POST_ACTION",
					payload: {
						postAction: async () => {
							await queryClient.invalidateQueries({ queryKey: ["seed-configurations"] });
							await refetchConfigurations();
							resetConfigAndData(setConfig, resetAllCustomizedData, resetRandomDataConfig);
							navigation.navigate(`/seed-company/${view || "seed-configuration-set-up"}`);
						},
					},
				});
				return;
			}
		} catch (error) {
			console.error(mode === "update" ? "Failed to update configuration:" : "Failed to run company only:", error);
			dispatchModal({
				type: "ERROR_MODAL",
				payload: { message: `Error: ${error.message}` },
			});
		}
	};

	return (
		<>
			<Spacer size={6} />
			<div>
				<Text variant="h3" weight="bold">
					Existing Seed Configurations
				</Text>

				<Spacer size={2} />
				<SelectConfigTable
					onEdit={handleConfigurationChange}
					onRun={handleRunConfiguration}
					onCopy={handleCopyConfiguration}
					onDelete={handleDeleteConfiguration}
				/>
			</div>
			<Spacer size={6} />
			<div>
				<Text variant="h1" weight="bold">
					Seed Configuration Settings
				</Text>
			</div>
			<Spacer size={2} />
			<Grid columns={["1fr", "1fr"]} gap={50}>
				<div className={css.left}>
					<div>
						<Text variant="h3" weight="bold">
							Seed Configuration Name
						</Text>
						<TextInput
							value={config.seedConfigurationName}
							onChange={(val) => handleConfigNameChange(val, config, setConfig)}
						/>
					</div>
					<Spacer size={2} />
					<div>
						<Text variant="h3" weight="bold">
							Environment
						</Text>
						<Select
							value={config.environment}
							onChange={(value) => handleEnvironmentChange(value, config, setConfig)}
							options={ENVIRONMENTS}
						/>
					</div>
					<Spacer size={2} />
					<div>
						<Text variant="h3" weight="bold">
							Company Name
						</Text>
						<TextInput
							value={config.seedCompanyName}
							onChange={(val) => setConfig({ ...config, seedCompanyName: val })}
						/>
					</div>
					<Spacer size={2} />
					<div>
						<Text variant="h3" weight="bold">
							Configuration Options
						</Text>
						<Spacer size={1} />
						<Text variant="caption" color="gray">
							Select the configurations options you want your company to include
						</Text>
						<Multiselect
							options={SEED_CONFIGURATION_OPTIONS}
							value={config.seedConfigurationOptions}
							onChange={(val) => handleConfigOptionsChange(val, config, setConfig)}
						/>
					</div>
				</div>
				<div>
					<RandomizedDataSection />
				</div>
			</Grid>
			<Spacer size={2} />
			<div>
				{config.seedConfiguration?.seedConfigurationId ? (
					<>
						<Button onClick={() => handleCreateOrUpdateCompany("update")}>
							Update Configuration and create Company
						</Button>
					</>
				) : (
					<>
						<Button
							onClick={() =>
								handleCreateConfigAndCompany(config, createConfiguration, dispatchModal, {
									setConfig,
									resetAllCustomizedData,
									resetRandomDataConfig,
									navigation,
									view,
								})
							}
						>
							Create Configuration and Company
						</Button>
						<Spacer size={2} />
						<Button
							color="green"
							onClick={() => {
								const configWithFullOptions = {
									...config,
									seedConfigurationOptions: config.seedConfigurationOptions
										.map((optionId) =>
											SEED_CONFIGURATION_OPTIONS.find((option) => option.id === optionId)
										)
										.filter(Boolean),
								};
								const configToUse = combineData(configWithFullOptions);
								handleCreateOrUpdateCompany("runOnly", configToUse);
							}}
						>
							Create Company Only
						</Button>
					</>
				)}
			</div>
			<Spacer size={10} />
			{modalState.isOpen && (
				<Modal
					isOpen={modalState.isOpen}
					showCloseButton={modalState.isCompanyCreated || modalState.isError}
					handleClose={async () => {
						dispatchModal({ type: "CLOSE_MODAL" });
						if (modalState.postAction) {
							await modalState.postAction();
						}
					}}
					content={
						<div>
							{modalState.isError ? (
								<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
									<Text variant="body" color="red">
										{modalState.message}
									</Text>
								</div>
							) : !modalState.isCompanyCreated ? (
								<>
									<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
										<Spinner size={40} />
									</div>
									<Spacer size={2} />
									<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
										<Text variant="h3" weight="bold" color="black">
											{modalState.message}
										</Text>
									</div>
								</>
							) : (
								<>
									<div>
										<div
											style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
										>
											<Text variant="h3" weight="bold" color="black">
												Company created successfully!
											</Text>
										</div>
										<Spacer size={2} />
										<div
											style={{ display: "flex", justifyContent: "center", alignItems: "center" }}
										>
											<Text variant="body" color="gray">
												{modalState.message.split("\n").map((line, idx) => (
													<React.Fragment key={idx}>
														{line}
														<br />
													</React.Fragment>
												))}
											</Text>
										</div>
									</div>
								</>
							)}
						</div>
					}
				/>
			)}
		</>
	);
};

export default SeedConfigurationSettings;
